import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Bell, Send, CheckCircle2, XCircle, Info, RefreshCw, MessageCircle } from "lucide-react";
import api, { apiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Switch } from "../components/ui/switch";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";

const CATEGORY_LABELS = {
  robot: { title: "Robot Trading", subs: { buy_executed: "BUY ejecutado", sell_executed: "SELL ejecutado", emergency_stop: "Emergency Stop activado" } },
  sync: { title: "Sincronización", subs: { binance_error: "Error Binance", coinbase_error: "Error Coinbase", other_platform_error: "Error otras plataformas" } },
  signals: { title: "Señales técnicas", subs: { strong_buy: "STRONG_BUY", strong_sell: "STRONG_SELL" } },
  fiscal: { title: "Fiscalidad", subs: { modelo_721: "Alerta Modelo 721 (>50.000€)" } },
  price: { title: "Precio", subs: { move_1h_pct: "Movimiento en 1h (%)", move_24h_pct: "Movimiento en 24h (%)" } },
  patrimonio: { title: "Patrimonio", subs: { delta_pct: "Variación patrimonio (%)", objetivo_alcanzado: "Objetivo alcanzado", objetivo_eur: "Objetivo (€)" } },
  tesoreria: { title: "Tesorería", subs: { deficit_7d: "Déficit previsto 7 días", deficit_15d: "Déficit previsto 15 días", deficit_30d: "Déficit previsto 30 días" } },
  core: {
    title: "Núcleo financiero",
    subs: {
      financiacion_termina_pronto: "Financiación próxima a finalizar",
      seguro_vence_pronto: "Seguro próximo a vencer",
      seguro_renovacion: "Seguro próximo a renovación",
      seguro_cambio_prima: "Seguro con cambio de prima",
      renovacion_vence_pronto: "Renovación próxima (dominio/hosting/SSL/licencia)",
      compromiso_vence_pronto: "Compromiso próximo a vencer",
      feature_activated: "Nueva funcionalidad activada por RadarUpFinance",
    },
  },
};

export default function Notificaciones() {
  const [cfg, setCfg] = useState(null);
  const [envOk, setEnvOk] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [log, setLog] = useState([]);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const [c, l] = await Promise.all([api.get("/notifications/config"), api.get("/notifications/log")]);
      setCfg(c.data.config);
      setEnvOk(c.data.telegram_env_configured);
      setLog(l.data.items || []);
    } catch (e) { toast.error(apiError(e)); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const updateCat = (cat, sub, val) => {
    const cats = { ...(cfg.categories || {}) };
    cats[cat] = { ...(cats[cat] || {}), [sub]: val };
    setCfg({ ...cfg, categories: cats });
    setDirty(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/notifications/config", {
        enabled: cfg.enabled,
        chat_id_override: cfg.chat_id_override || "",
        categories: cfg.categories,
      });
      setCfg(data.config);
      setDirty(false);
      toast.success("Configuración guardada");
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  const sendTest = async () => {
    try {
      const { data } = await api.post("/notifications/test");
      if (data.ok) toast.success("Mensaje de prueba enviado a Telegram");
      else toast.error(data.error || "No se pudo enviar");
    } catch (e) { toast.error(apiError(e)); }
  };

  if (!cfg) return <div className="p-6 text-sm text-muted-foreground">Cargando…</div>;

  return (
    <div className="space-y-6" data-testid="notificaciones-page">
      <div>
        <h1 className="font-heading text-3xl font-extrabold tracking-tighter flex items-center gap-3">
          <Bell size={26} /> Notificaciones
        </h1>
        <p className="text-sm text-muted-foreground mt-1">Alertas Telegram para eventos financieros y del robot cripto.</p>
      </div>

      {/* Estado de configuración */}
      <div className="rounded-2xl border border-border bg-card p-5">
        <h3 className="font-semibold mb-3 flex items-center gap-2"><MessageCircle size={16} /> Estado Telegram</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <div className="text-xs text-muted-foreground">TELEGRAM_BOT_TOKEN (.env)</div>
            <div className="mt-1">
              {envOk ? <Badge className="bg-emerald-500/15 text-emerald-700 border-emerald-500/30"><CheckCircle2 size={12} className="mr-1" /> Configurado</Badge>
                     : <Badge variant="outline"><XCircle size={12} className="mr-1" /> No configurado</Badge>}
            </div>
          </div>
          <div>
            <div className="text-xs text-muted-foreground">Chat ID (opcional)</div>
            <Input
              value={cfg.chat_id_override || ""}
              onChange={(e) => { setCfg({ ...cfg, chat_id_override: e.target.value }); setDirty(true); }}
              placeholder="Usar TELEGRAM_CHAT_ID de .env"
              className="mt-1 h-9"
              data-testid="notif-chat-id"
            />
          </div>
          <div>
            <div className="text-xs text-muted-foreground">Notificaciones activadas</div>
            <div className="mt-2 flex items-center gap-3">
              <Switch checked={!!cfg.enabled} onCheckedChange={(v) => { setCfg({ ...cfg, enabled: v }); setDirty(true); }} data-testid="notif-master-switch" />
              <span className="text-sm">{cfg.enabled ? "ON" : "OFF"}</span>
            </div>
          </div>
        </div>
        <div className="mt-4 flex items-center gap-2 flex-wrap">
          <Button onClick={save} disabled={!dirty || saving} data-testid="notif-save">
            {saving ? "Guardando…" : "Guardar cambios"}
          </Button>
          <Button variant="outline" onClick={sendTest} disabled={!envOk} data-testid="notif-test">
            <Send size={14} className="mr-2" /> Enviar mensaje de prueba
          </Button>
          <Button variant="outline" onClick={load}>
            <RefreshCw size={14} className="mr-2" /> Refrescar
          </Button>
          {dirty && <span className="text-xs text-amber-600">Cambios sin guardar</span>}
        </div>
        {!envOk && (
          <div className="mt-4 rounded-xl border border-amber-500/40 bg-amber-500/10 p-3 text-xs flex items-start gap-2">
            <Info size={14} className="mt-0.5" />
            <div>
              Para activar Telegram: crea un bot con <b>@BotFather</b>, copia el token y añádelo como <code>TELEGRAM_BOT_TOKEN</code> en las variables de entorno del servidor. Obtén tu Chat ID con <b>@userinfobot</b>.
            </div>
          </div>
        )}
      </div>

      {/* Toggles por categoría */}
      <div className="space-y-4">
        {Object.entries(CATEGORY_LABELS).map(([cat, meta]) => (
          <div key={cat} className="rounded-2xl border border-border bg-card p-5" data-testid={`notif-cat-${cat}`}>
            <h3 className="font-semibold mb-3">{meta.title}</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {Object.entries(meta.subs).map(([sub, label]) => {
                const val = cfg.categories?.[cat]?.[sub];
                const isNumeric = ["move_1h_pct", "move_24h_pct", "delta_pct", "objetivo_eur"].includes(sub);
                if (isNumeric) {
                  return (
                    <div key={sub} className="flex items-center justify-between gap-3 p-3 rounded-xl border border-border">
                      <div className="flex-1">
                        <div className="text-sm font-medium">{label}</div>
                        <div className="text-[10px] text-muted-foreground">0 = desactivado</div>
                      </div>
                      <Input
                        type="number"
                        step="0.1"
                        value={val ?? 0}
                        onChange={(e) => updateCat(cat, sub, parseFloat(e.target.value) || 0)}
                        className="w-24 h-9 tabular-nums"
                        data-testid={`notif-${cat}-${sub}`}
                      />
                    </div>
                  );
                }
                return (
                  <div key={sub} className="flex items-center justify-between gap-3 p-3 rounded-xl border border-border">
                    <div className="text-sm">{label}</div>
                    <Switch
                      checked={!!val}
                      onCheckedChange={(v) => updateCat(cat, sub, v)}
                      data-testid={`notif-${cat}-${sub}`}
                    />
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      {/* Historial */}
      <div className="rounded-2xl border border-border bg-card">
        <div className="p-5 pb-3 font-semibold">Últimas notificaciones ({log.length})</div>
        {log.length === 0 ? (
          <div className="p-6 text-sm text-muted-foreground text-center">Sin eventos registrados aún.</div>
        ) : (
          <div className="max-h-96 overflow-y-auto divide-y divide-border">
            {log.map((n, i) => (
              <div key={i} className="p-4 flex items-start gap-3">
                <div className={`mt-1 h-2 w-2 rounded-full ${n.sent ? "bg-emerald-500" : "bg-muted-foreground"}`} />
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-sm">{n.title}</div>
                  {n.body && <div className="text-xs text-muted-foreground mt-0.5 whitespace-pre-line">{n.body}</div>}
                  <div className="text-[10px] text-muted-foreground mt-1 font-mono">
                    {new Date(n.created_at).toLocaleString("es-ES")} · {n.category}/{n.subcategory} · {n.sent ? "Enviada" : "No enviada"}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
